import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Users, Bot, Building2, Wallet, ArrowRight, Check, Loader2,
  Code, Shield, Globe, Zap, ChevronRight, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  {
    id: 'framework',
    name: 'Framework Partners',
    subtitle: 'Popular agent frameworks & more',
    icon: Code,
    color: 'blue',
    desc: 'Native integration with your agent framework. Kojiru credit as a built-in tool for every agent your users deploy.',
    benefits: [
      'Co-branded documentation and tutorials',
      'Native SDK integration support',
      'Joint marketing and developer events',
      'Revenue share on agent transactions',
      'Early access to protocol features',
    ],
  },
  {
    id: 'lender',
    name: 'Lender Partners',
    subtitle: 'Institutions extending credit to agents',
    icon: Wallet,
    color: 'emerald',
    desc: 'Deploy capital into the agent economy. Evaluate agents via ACS scores and extend bilateral credit lines with full risk transparency.',
    benefits: [
      'Direct bilateral credit lines (not pooled)',
      'ACS-based risk assessment framework',
      'Institutional API access ($5K+/mo tier)',
      'Priority evaluator marketplace access',
      'Dedicated account management',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Partners',
    subtitle: 'Companies deploying agent fleets',
    icon: Building2,
    color: 'amber',
    desc: 'Deploy and manage fleets of Kojiru-Certified agents with enterprise-grade credit infrastructure, compliance, and monitoring.',
    benefits: [
      'Fleet management dashboard',
      'Bulk agent registration and staking',
      'White-label AaaS (Work-Pods, SOPs, Memory)',
      'Custom SOP development',
      'Enterprise SLA and priority support',
    ],
  },
];

export default function Partners() {
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', partner_type: 'framework',
    website: '', description: '', agent_count: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email || !form.description) { setError('Please fill in all required fields.'); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/partners/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, agent_count: form.agent_count ? parseInt(form.agent_count) : null }),
      });
      const data = await res.json();
      if (res.ok) { setResult(data); }
      else { setError(data.detail || 'Submission failed'); }
    } catch (err) { setError('Network error.'); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Helmet><title>Partnerships: Kojiru Agent Credit Protocol</title></Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
              <Users className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">Partnership Program</span>
            </div>
            <h1 data-testid="partners-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">
              Build the Machine Economy{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-300 bg-clip-text text-transparent">Together.</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Join the Kojiru partnership program. Whether you build agent frameworks, provide institutional capital, or deploy enterprise agent fleets: there's a tier for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partnership Tiers */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-5">
          {TIERS.map(tier => (
            <div key={tier.id} className={`p-6 rounded-xl bg-${tier.color}-500/5 border border-${tier.color}-500/20`} data-testid={`tier-${tier.id}`}>
              <tier.icon className={`w-8 h-8 text-${tier.color}-400 mb-3`} />
              <h3 className="text-lg font-bold mb-0.5">{tier.name}</h3>
              <p className={`text-xs text-${tier.color}-400 mb-3`}>{tier.subtitle}</p>
              <p className="text-xs text-gray-400 mb-4">{tier.desc}</p>
              <ul className="space-y-2">
                {tier.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[11px] text-gray-400">
                    <Check className={`w-3 h-3 text-${tier.color}-400 shrink-0 mt-0.5`} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 px-4 border-t border-white/[0.04]" id="apply">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-6">Apply for Partnership</h2>

          {result ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="p-8 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-center" data-testid="partner-success">
              <Check className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-emerald-400 mb-2">Application Received</h3>
              <p className="text-sm text-gray-400 mb-2">{result.message}</p>
              <p className="text-xs text-gray-600 font-mono">{result.application_id}</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Company Name *</label>
                  <input data-testid="partner-company" required value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Contact Name *</label>
                  <input data-testid="partner-name" required value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Email *</label>
                  <input data-testid="partner-email" type="email" required value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Partnership Type *</label>
                  <select data-testid="partner-type" value={form.partner_type} onChange={e => setForm({ ...form, partner_type: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none">
                    <option value="framework">Framework Partner</option>
                    <option value="lender">Lender Partner</option>
                    <option value="enterprise">Enterprise Partner</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Website</label>
                  <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Planned Agent Count</label>
                  <input type="number" min="0" value={form.agent_count} onChange={e => setForm({ ...form, agent_count: e.target.value })}
                    placeholder="How many agents?" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Describe your partnership goals *</label>
                <textarea data-testid="partner-desc" required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={4} placeholder="Tell us about your organization and how you'd like to work with Kojiru..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-purple-500 focus:outline-none resize-y" />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              <Button data-testid="partner-submit" type="submit" disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 py-3">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : <><Users className="w-4 h-4 mr-2" /> Submit Partnership Application</>}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-gray-500 mb-4">Not ready to partner? Start building today.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/developers"><Button variant="outline" className="border-white/10"><Code className="w-4 h-4 mr-2" /> Developer Docs</Button></Link>
            <Link to="/register-agent"><Button className="bg-emerald-600 hover:bg-emerald-700"><Bot className="w-4 h-4 mr-2" /> Register an Agent</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
